import PopupBasic from 'src/game/components/popups/PopupBasic';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import View from '@play-co/timestep-core/lib/ui/View';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { parseAmount } from 'src/lib/utils';
import sounds from 'src/lib/sounds';
import { animateGems } from '../../animation/CurrencyAnimation';
import { Feature } from 'src/replicant/analytics';
import { appUpdateReward } from 'src/replicant/native/native.ruleset';
import { AssetGroup } from '@play-co/timestep-core';
import popups from 'src/lib/ui/config/popups';

const ui = uiConfig.buruburu.invite.confirm;

export default class PopupUpdateAppIncentivisedReward extends PopupBasic {
  private gemsReward: {
    label: LangBitmapFontTextView;
    animate: () => Promise<void>;
  };

  constructor(opts: { superview: View; close: () => void }) {
    super({
      superview: opts.superview,
      close: opts.close,
      zIndex: 10000,
      width: popups.buruburu.width,
      height: 690,
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.UPDATE_APP_POPUP_INCENTIVISED_REWARD,
      },
    });

    this.title.updateOpts({ size: 36 });
    this.buttonClose.removeFromSuperview();

    // Chest image with gem reward
    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/updateApp/update_reward_chest.png',
      width: 238,
      height: 224,
      x: this.box.style.width / 2,
      y: 210,
      centerOnOrigin: true,
    });

    // Gem reward row below chest
    this.gemsReward = this.createRewardView(this.box, {
      y: 390,
      type: 'gems',
      value: appUpdateReward.gems,
    });

    // Message below gem row
    this.message.updateOpts({
      width: this.box.style.width - 80,
      height: 100,
      x: 40,
      y: 455,
      size: 28,
      zIndex: 1,
    });

    // Green collect button
    new ButtonScaleViewWithText({
      ...ui.okButton,
      superview: this.box,
      labelOffsetX: -4,
      labelOffsetY: -1,
      fontSize: 38,
      x: this.box.style.width / 2,
      y: this.box.style.height - 35 - 80 / 2,
      width: 308,
      height: 87,
      centerOnOrigin: true,
      localeText: () => i18n('native.updateIncentivised.reward.cta'),
      onClick: async () => {
        sounds.playSound('ok');

        await this.gemsReward.animate();

        opts.close();
      },
      tracking: {
        feature: Feature.NATIVE._,
        $subFeature: Feature.NATIVE.UPDATE_APP_POPUP_INCENTIVISED_REWARD,
      },
    });
  }

  /**
   * Create reward view
   */
  private createRewardView(
    superview: View,
    opts: { y: number; value: number; type: 'gems' },
  ) {
    const reward = new ImageSliceView({
      superview,
      image: 'assets/ui/shared/frames/inset_panel.png',
      slices: {
        left: 25,
        right: 25,
        top: 25,
        bot: 25,
      },
      centerOnOrigin: true,
      x: this.box.style.width / 2,
      y: opts.y,
      height: 100,
      width: 290,
    });

    const icon = new ImageScaleView({
      superview: reward,
      scaleMethod: 'contain',
      image: 'assets/ui/hud/icons/hud_gem.png',
      width: 50,
      height: 42,
      scale: 1.7,
      x: 15,
      y: 16,
    });

    const label = new LangBitmapFontTextView({
      superview: reward,
      size: 45,
      font: 'Body',
      height: 68,
      width: 196,
      verticalAlign: 'center',
      align: 'center',
      localeText: () => `x ${parseAmount(opts.value)}`,
      x: 85,
      y: 12,
    });

    const animate = async () => {
      const iconPos = icon.getPosition(this.root);
      const start = {
        x: iconPos.x + icon.style.width / 2,
        y: iconPos.y + icon.style.height / 2,
      };

      await animateGems({
        superview: this.root,
        start: { position: start, scale: 1 },
        count: opts.value,
      });
    };

    return { label, animate };
  }

  protected getAssetsGroup() {
    return new AssetGroup([
      'assets/ui/updateApp/update_reward_chest.png',
      'assets/ui/shared/frames/inset_panel.png',
      'assets/ui/hud/icons/hud_gem.png',
    ]);
  }

  public init() {
    super.init({
      title: i18n('native.updateIncentivised.reward.title'),
      message: i18n('native.updateIncentivised.reward.message'),
    });

    sounds.playSound('applause');
  }
}
